// ДОКУМЕНТ OPEN ШАГА 7 — разбор в новом окне из памяти шага (B9-6 `3ed19875cbb88192a55bd5368444a8a3`,
// решение `s965.d1`).
//
// ⭐ СОБИРАЕТ ОКНО-ОТКРЫВАТЕЛЬ, НЕ НОВОЕ ОКНО. Паспорт уже лежит в памяти шага 7; новое окно получает
// готовую строку HTML через `Blob` URL и хранилище не читает вовсе. Поэтому Open работает и после
// `sent: true` (ключ стёрт, `s889.d3`), и без хранилища (`STORAGE_NOTICE`), и после подъёма версии конверта.
//
// ⭐ ВТОРОГО РЕНДЕРЕРА НЕТ: разметка — `assembleSections(passport)` (`assemble.jsx`), тот же план и
// перенумерация, что у паспорта. На пути визарда System 2 нет → §VI снимается, §VII идёт шестым (`s882.d1`).
//
// ⭐ CSS ПАСПОРТА — ТОЛЬКО ВНУТРИ ДОКУМЕНТА. Шесть `r10*.css` у раздела свои `:root`; на маршрут визарда
// их не подключать (шапка `wizard-tokens.css`) — здесь они строкой, `?raw`.

import { assembleSections } from '../../render/assemble.jsx';
import r10 from '../../styles/r10.css?raw';
import r10ii from '../../styles/r10-ii.css?raw';
import r10iv from '../../styles/r10-iv.css?raw';
import r10v from '../../styles/r10-v.css?raw';
import r10vi from '../../styles/r10-vi.css?raw';
import r10vii from '../../styles/r10-vii.css?raw';
import { FONTS, PAGE_H, PAGE_W, fitScript } from './s7Cover.js';
import { localIsoDate } from '../../render/docDate.js';
import { fitDocument, measureSectionII, settleSectionII } from '../../render/docFit.js';
import { SAVE_PDF_CSS, attachSaveButton, saveButtonHtml } from '../../pdf/saveButton.js';

/** Шесть таблиц паспорта — те же, что подключает стенд (`PassportDev.jsx`). */
export const DOC_CSS = [r10, r10ii, r10iv, r10v, r10vi, r10vii];

/** Лист A4 в CSS-пикселях (96 dpi): 210 × 297 мм. */
const A4_W = 210 / 25.4 * 96;
const A4_H = 297 / 25.4 * 96;

/**
 * Запас под поля печати, мм на сторону. Печать iOS (любой браузер — там один движок WebKit и системный
 * диалог) кладёт свои поля и `@page{margin:0}` не слушает. Значение подобрано, не замерено.
 */
export const PRINT_SAFE_MM = 12;

/** Масштаб листа, вниз до 1e-4: меньшая из сторон страницы `w×h` за вычетом запаса. */
const fitScale = (w, h, safe) => Math.floor(Math.min((w - 2 * safe) / PAGE_W, (h - 2 * safe) / PAGE_H) * 1e4) / 1e4;

/** Масштаб листа при печати в Chromium: страница A4 в CSS px (96 dpi). */
export const PRINT_SCALE = fitScale(A4_W, A4_H, PRINT_SAFE_MM / 25.4 * 96);

/**
 * ⭐ Масштаб листа при печати iOS: там страница в CSS px — это A4 в ПУНКТАХ (595×842), а не в 96 dpi.
 * Замер cc-203 на симуляторе (Safari, iOS 26.5): масштаб Chromium 0.5669 давал 12 страниц на 6 листов,
 * 0.44 — ровно 6. Различает движки ширина страницы печати: `@media print and (max-width: …)`.
 */
export const PRINT_SCALE_PT = fitScale(210 / 25.4 * 72, 297 / 25.4 * 72, PRINT_SAFE_MM / 25.4 * 72);

/** Порог ширины страницы печати, CSS px: уже — печать iOS (A4 в пунктах), шире — Chromium (A4 в 96 dpi). */
export const PRINT_PT_MAX_WIDTH = 700;

/** Фон листа — то, с чем печать iOS «смешивает» полупрозрачное (см. `PRINT_FLAT`). */
const PRINT_BG = [10, 11, 13];

/** Цвет `hex` с прозрачностью `a`, заранее положенный на фон листа, — непрозрачный `#rrggbb`. */
function onBg(hex, a) {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${c.map((v, k) => Math.round(v * a + PRINT_BG[k] * (1 - a)).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * ⭐ ПОЛУПРОЗРАЧНЫЕ ГРАДИЕНТЫ ПЕЧАТЬ iOS КЛАДЁТ НЕПРОЗРАЧНЫМИ — и CSS (`rgba` в `radial-gradient`), и SVG
 * (`stop-opacity`). Замер cc-203: подложка `.ab-paper` (`.10`) — огромный оранжевый эллипс на обложке,
 * заливка силуэта `#fill-i` (`.32 → .08`) — плотная полоса под линией. Разметка и `r10*.css` — канон CD,
 * их не трогаем: в печати iOS стопы перекрашиваются в тот же цвет, заранее смешанный с фоном листа
 * (`stop-color` — CSS-свойство), с `stop-opacity:1`. Таблица — все градиенты документа с прозрачностью
 * (id детерминированы рендером; `ptn-v-N` — по карточке §V). Неучтённый id останется плотным — гард в тесте.
 */
export const PRINT_FLAT = [
  ['fill-i', [['#D48A3C', 0.32], ['#C8482B', 0.08]]],
  ['halo-i', [['#C8482B', 0.55], ['#C8482B', 0.10], ['#C8482B', 0]]],
  ['pro-fade-iii', [['#0e0f12', 0], ['#0e0f12', 0.4]]],
  ['rl-iv', [['#7A1E10', 0.55], ['#C8482B', 1], ['#E8623F', 1], ['#C8482B', 0.75]]],
  ['ptn-v-', [['#7A1E10', 0], ['#7A1E10', 0.22]]],
  ['cl-fill', [['#D48A3C', 0.22], ['#C8482B', 0.05]]],
  ['cl-now-halo', [['#C8482B', 0.55], ['#C8482B', 0.10], ['#C8482B', 0]]],
  ['cl-moss-halo', [['#8FA168', 0.55], ['#8FA168', 0.10], ['#8FA168', 0]]],
];

const flatCss = PRINT_FLAT.map(([id, stops]) => stops.map(([hex, a], i) => {
  const sel = id.endsWith('-') ? `[id^="${id}"]` : `[id="${id}"]`;
  return `${sel} stop:nth-child(${i + 1}){stop-color:${onBg(hex, a)}!important;stop-opacity:1!important}`;
}).join('')).join('');

/**
 * ПЕЧАТЬ ОКНА OPEN — вариант E замера `3f219875cbb8819cb6dcf1f5c5ef2fb5`, доделан под iOS тикетом
 * `3f319875cbb881029e95e00d097722cb` (`s977.d3`, cc-203). Только `@media print`, экран не меняется ни на байт.
 *
 * ДВЕ СХЕМЫ ПО ДВИЖКАМ, различает их ширина страницы печати (`PRINT_PT_MAX_WIDTH`):
 * - Chromium (страница A4 в 96 dpi): `zoom` на стопке — лист на страницу, проверено e2e (`page.pdf`).
 *   Схема iOS тут не годится: Chromium режет лист по ИСХОДНОЙ высоте раскладки (замер cc-203: 12 страниц).
 * - iOS (страница A4 в пунктах): ⛔ НЕ `zoom`. В Safari (iOS 26.5, замер на симуляторе) `zoom` НЕ уменьшает
 *   мелкий текст (метка §II 10px оставалась в исходном размере — сетка `64px 1fr auto` распиралась,
 *   заголовок ломался по слову), а `text-size-adjust` любой, кроме `auto`, отменяет зум текста ЦЕЛИКОМ
 *   (обложка вылезала за лист на 37 %). Поэтому лист масштабируется `transform`, а чтобы печать резала по
 *   масштабированному месту, родитель листов — сетка со строкой «лист × масштаб» и `overflow:hidden`
 *   (обёртка §II без класса — `display:contents`, её листы становятся строками той же сетки).
 *
 * Лист = страница с запасом `PRINT_SAFE_MM`. Фон печатается и при выключенной «фоновой графике»
 * (`print-color-adjust: exact`). Зерно `.ab-grain` и подложка `.ab-paper` гасятся (зерно — вес PDF ~2 МБ
 * вместо ~19; подложка — эллипс на iOS, `s977.d3`).
 */
export const PRINT_CSS = '@media print{'
  + '@page{size:A4;margin:0}'
  + 'html,body{background:#0a0b0d!important}'
  + '.s7-docwrap{height:auto!important;overflow:visible!important}'
  + `.s7-doc{transform:none!important;width:${PAGE_W}px!important;zoom:${PRINT_SCALE};margin:0 auto}`
  + '*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}'
  + '.artboard{break-inside:avoid;break-after:page}'
  + '.s7-doc>div:last-child .artboard:last-of-type{break-after:auto}'
  + '.ab-grain,.ab-paper{display:none!important}'
  + '}'
  + `@media print and (max-width:${PRINT_PT_MAX_WIDTH}px){`
  + `.s7-doc{zoom:1;width:${Math.round(PAGE_W * PRINT_SCALE_PT)}px!important}`
  + `.s7-doc>div{display:grid;grid-auto-rows:${Math.round(PAGE_H * PRINT_SCALE_PT * 100) / 100}px;overflow:hidden;break-after:page}`
  + '.s7-doc>div:last-child{break-after:auto}'
  + '.s7-doc>div>div:not(.artboard){display:contents}'
  + `.artboard{transform:scale(${PRINT_SCALE_PT});transform-origin:0 0;break-after:auto}`
  + '.cv-chart{background:none!important}'
  + `.st-card-now{background:linear-gradient(180deg,${onBg('#7A1E10', 0.10)},${onBg('#14120F', 0.45)})!important}`
  + flatCss
  + '}';

/**
 * Полный HTML окна Open: разделы паспорта стопкой артбордов A4, ужатой по ширине окна.
 *
 * `opts.plans.II` — план листов §II, замеренный `paginateWindow` (без него — один лист, как в SSR).
 * `opts.generatedAt` — дата документа (по умолчанию сегодня, `docDate.js`).
 * `opts.pdf` — склад PDF (`pdfStore.js`): с ним в углу стоит кнопка «Save PDF» (`saveButton.js`). Без него
 * кнопки нет вовсе — так документ собирает сервер PDF (строка 67).
 * ⭐ ПОДГОНКА — В САМОМ ОКНЕ (тикет паспорта s969): после шрифтов `fitDocument` (`docFit.js`) — лестница
 * §IV CD, лестница §VII, подписи §III/§IV, штампы перелива. Конец помечается `html[data-fit=done]`.
 */
export function documentHtml(passport, opts = {}) {
  const sections = assembleSections(passport, {
    generatedAt: opts.generatedAt || localIsoDate(),
    ...(opts.plans ? { plans: opts.plans } : {}),
  });
  return '<!doctype html><html><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<link rel="stylesheet" href="${FONTS}">`
    + DOC_CSS.map((css) => `<style>${css}</style>`).join('')
    + '<style>html,body{margin:0;background:#0a0b0d;overflow-x:hidden}'
    + `.s7-docwrap{overflow:hidden}.s7-doc{width:${PAGE_W}px;transform-origin:0 0}</style>`
    + `<style>${PRINT_CSS}</style>`
    + (opts.pdf ? `<style>${SAVE_PDF_CSS}</style>` : '')
    + `</head><body>${opts.pdf ? saveButtonHtml(opts.pdf.status) : ''}<div class="s7-docwrap"><div class="s7-doc">${sections.map((h) => `<div>${h}</div>`).join('')}</div></div>`
    + `${fitScript('.s7-doc', { stack: true })}`
    + `<script>(function(){var f=${fitDocument};function run(){f(document);document.documentElement.setAttribute('data-fit','done')}`
    + `(document.fonts&&document.fonts.ready?document.fonts.ready:Promise.resolve()).then(run)})()</`
    + 'script></body></html>';
}

/**
 * ПАГИНАЦИЯ §II ДОКУМЕНТА В ОКНЕ `win` — из окна-открывателя (тикет паспорта s969). Окно получает
 * статику, а план листов зависит от замера (s368.d2): дождаться подгонки окна → замерить §II →
 * перерисовать документ с планом → повторить до неподвижной точки. Окно Blob того же origin, что и
 * открыватель, поэтому `win.document` доступен. Любая ошибка (окно закрыли, чужой origin) — тишина:
 * документ остаётся одним листом, как был.
 *
 * @returns {Promise<number[]|null>} итоговый план §II (null — пересборка не понадобилась)
 */
export async function paginateWindow(win, passport, opts = {}, env = globalThis) {
  const fitted = () => new Promise((resolve, reject) => {
    const t0 = Date.now();
    (function poll() {
      try {
        if (win.closed) return reject(new Error('closed'));
        const d = win.document;
        if (d && d.documentElement && d.documentElement.getAttribute('data-fit') === 'done') return resolve(d);
      } catch (e) { return reject(e); }
      if (Date.now() - t0 > 15000) return reject(new Error('timeout'));
      env.setTimeout(poll, 50);
    })();
  });
  try {
    return await settleSectionII(
      async () => measureSectionII(await fitted()),
      async (plan) => {
        const d = win.document;
        d.open();
        d.write(documentHtml(passport, { ...opts, plans: { II: plan } }));
        d.close();
      },
    );
  } catch {
    return null;
  }
}

/**
 * Открыть документ в новом окне. Синхронно — зовётся прямо из обработчика тапа, иначе `window.open`
 * режет блокировщик. Прежний Blob URL отзывается при следующем Open, новый — нет (`s965.d1` п. 7).
 * `window.open` вернул `null` (блокировщик) — ничего не печатаем (п. 6), новый URL отзывается сразу.
 *
 * `pdf` — склад PDF (`pdfStore.js`): документ получает кнопку «Save PDF», сборка этого паспорта
 * заказывается (если ещё не собран), кнопка подключается к складу.
 *
 * `build: false` — кнопка подключается, но сборка не заказывается: шаг 7 ещё ждёт судью цитат (`s998.d3`) и
 * закажет её сам.
 *
 * @returns {string|null} URL открытого документа — его хранит вызывающий до следующего Open
 */
export function openDocument(passport, prevUrl = null, env = globalThis, pdf = null, { build = true } = {}) {
  // одна дата на первую отрисовку и пересборки §II
  const opts = { generatedAt: localIsoDate(), ...(pdf ? { pdf } : {}) };
  if (build) pdf?.prebuild(passport, opts.generatedAt);
  const url = env.URL.createObjectURL(new env.Blob([documentHtml(passport, opts)], { type: 'text/html' }));
  const win = env.open(url, '_blank');
  if (!win) {
    env.URL.revokeObjectURL(url);
    return prevUrl;
  }
  if (prevUrl) env.URL.revokeObjectURL(prevUrl);
  if (pdf) attachSaveButton(win, pdf, env);
  // §II по замеру — после открытия, асинхронно (само открытие обязано быть синхронным)
  if (win.document) paginateWindow(win, passport, opts, env);
  return url;
}
